import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'

export interface WarrantyRegistrationRow {
  warranty_uuid: string
  holding_uuid: string
  registration_number: string
  // ISO timestamp - null only for malformed warranties. Shown as the
  // "registered on" date in the list.
  active_from: string | null
  // Enum value ("MANUFACTURER_WARRANTY", …); the frontend
  // humanises it for display.
  warranty_type: string
  // Class name ("Platinum", "Preferred", …). Always a string; the
  // backend falls back to "Uncategorised" when a template is missing
  // a warranty_category.
  warranty_category_name: string
  template_name: string
  policy_code: string
  product_name: string
  consumer_name: string
  consumer_email: string
  // Downstream org name for the specific branch that captured the
  // registration. Useful on HQ rollups where a single category may
  // span multiple locations.
  location_name: string
}

/**
 * GET /organisations/{orgUuid}/partnerships/{uuid}/warranty-registrations-summary/
 *
 * One row per active warranty registration tied to the partnership
 * (or any of its sub-locations when the partnership is an HQ).
 */
export function usePartnershipWarrantyRegistrations(
  partnershipUuid: string | undefined,
) {
  const { orgUuid } = useOrg()
  return useQuery<WarrantyRegistrationRow[]>({
    queryKey: [
      'partnership-warranty-registrations-summary',
      orgUuid,
      partnershipUuid,
    ],
    queryFn: () =>
      api
        .get(
          `/organisations/${orgUuid}/partnerships/${partnershipUuid}/warranty-registrations-summary/`,
        )
        .then((r) => {
          const payload = r.data.result ?? r.data
          return payload.results ?? payload
        }),
    enabled: !!orgUuid && !!partnershipUuid,
  })
}
